import { useState, useEffect, useCallback } from 'react'
import { authClient } from '@/lib/api/auth.client'
import type { WorkOSUser } from '@super/shared-types'
import { AUTH_ERROR_CODES, AuthenticationError } from '@super/shared-types'

export type SignInOptions = { state?: string; mode?: 'signin' | 'signup' }

interface UseAuthReturn {
  user: WorkOSUser | null
  isSuperAdmin: boolean
  isLoading: boolean
  signIn: (opts?: SignInOptions) => void
  signOut: () => void
  getAccessToken: () => Promise<string | null> // Required for WorkOS widgets only
}

/**
 * Custom auth hook that replaces WorkOS useAuth
 * Uses backend session management with encrypted cookies
 *
 * This implementation follows the WorkOS Node.js backend pattern:
 * - Session validation via encrypted cookies (wos-session)
 * - Server-side session refresh when access tokens expire
 * - No frontend token management - cookies are sent automatically
 * - Backend extracts access tokens from encrypted session for API requests
 */
export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<WorkOSUser | null>(null)
  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [widgetToken, setWidgetToken] = useState<string | null>(null)

  // Check session status from backend
  const checkSession = useCallback(async () => {
    try {
      const session = await authClient.getSession()

      if (session.authenticated && session.user) {
        // Only update state if the user has actually changed
        setUser(prevUser => {
          // If same user ID, keep previous reference to prevent cascading re-renders
          if (prevUser?.id === session.user!.id) {
            return prevUser
          }
          return session.user!
        })

        setIsSuperAdmin(prevIsSuperAdmin => {
          const newIsSuperAdmin = session.isSuperAdmin || false
          return newIsSuperAdmin === prevIsSuperAdmin
            ? prevIsSuperAdmin
            : newIsSuperAdmin
        })
      } else {
        setUser(null)
        setIsSuperAdmin(false)
      }
    } catch (error) {
      const authError =
        error instanceof AuthenticationError
          ? error
          : new AuthenticationError(
              AUTH_ERROR_CODES.SESSION_INVALID,
              error instanceof Error ? error.message : 'Unknown error',
            )
      console.error('Failed to check session:', authError.toJSON())
      setUser(null)
      setIsSuperAdmin(false)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkSession()

    // Check session periodically - backend handles token refresh automatically
    // Check every 10 minutes to catch expiring sessions before API calls fail
    const interval = setInterval(checkSession, 10 * 60 * 1000) // Every 10 minutes

    return () => clearInterval(interval)
  }, [checkSession])

  const signIn = useCallback((opts?: SignInOptions) => {
    authClient.signIn(opts)
  }, [])

  const signOut = useCallback(() => {
    // Redirect to backend logout endpoint
    // Backend clears session cookie and redirects to WorkOS logout
    authClient.signOut()
  }, [])

  // Get access token for WorkOS widgets with simple caching
  const getAccessToken = useCallback(
    async (scopes?: string[]): Promise<string | null> => {
      // Return cached token if available
      if (widgetToken) {
        return widgetToken
      }

      // Generate a fresh widget token from the backend
      try {
        const result = await authClient.generateWidgetToken(scopes)
        if (result.success && result.token) {
          setWidgetToken(result.token)
          return result.token
        }
        const authError = new AuthenticationError(
          AUTH_ERROR_CODES.WIDGET_TOKEN_FAILED,
          result.error,
        )
        console.error('Failed to generate widget token:', authError.toJSON())
        return null
      } catch (error) {
        const authError =
          error instanceof AuthenticationError
            ? error
            : new AuthenticationError(
                AUTH_ERROR_CODES.NETWORK_ERROR,
                error instanceof Error ? error.message : 'Unknown error',
              )
        console.error('Failed to get access token:', authError.toJSON())
        return null
      }
    },
    [widgetToken],
  )

  return {
    user,
    isSuperAdmin,
    isLoading,
    signIn,
    signOut,
    getAccessToken,
  }
}
